import React, { useState, useEffect } from 'react';
import { useLocation, useRoute } from 'wouter';
import { useUser } from '@/contexts/UserContext';
import { api } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Download,
  Share2,
  Calendar,
  Camera,
  User,
  FileImage,
  MapPin,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Photo {
  id: string;
  filename: string;
  storageurl: string;
  thumbnailurl: string;
  resolution: string;
  processingstatus: string;
  visibility: string;
  author?: string;
  capture_datetime?: string;
  camera_model?: string;
  original_filename?: string;
  bibnumbers?: string;
  metadata: {
    cameraModel?: string;
    author?: string;
    originalName?: string;
    fileSize?: number;
    captureDateTime?: string;
    gpsLatitude?: number;
    gpsLongitude?: number;
    technical?: any;
  };
  uploadedat: string;
  createdat: string;
  batchid?: string;
}

const PhotoPreviewPage: React.FC = () => {
  const [, setLocation] = useLocation();
  const [, params] = useRoute('/photo/:photoId');
  const { user, isLoading } = useUser();

  const [photo, setPhoto] = useState<Photo | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);

  const photoId = params?.photoId;

  // Buscar dados da foto
  const fetchPhoto = async () => {
    if (!photoId || !user?.id) return;

    try {
      setLoading(true);
      setFetchError(false);
      const response = await api.get(`/api/photos/${photoId}`);

      if (response.data.success) {
        setPhoto(response.data.data);
      } else {
        throw new Error('Foto não encontrada');
      }
    } catch (error) {
      console.error('Erro ao buscar foto:', error);
      setFetchError(true);
      toast({
        title: "Erro ao carregar foto",
        description: "Não foi possível carregar a foto solicitada.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Buscar dados quando o componente montar
  useEffect(() => {
    if (user?.id && photoId) {
      fetchPhoto();
    }
  }, [user?.id, photoId]);

  // Redirecionar se não estiver logado
  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
    }
  }, [isLoading, user, setLocation]);

  const handleDownload = () => {
    if (!photo) return;

    // Criar link de download
    const link = document.createElement('a');
    link.href = photo.storageurl;
    link.download = photo.metadata?.originalName || photo.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Download iniciado",
      description: "O download da foto foi iniciado.",
    });
  };

  const handleShare = () => {
    if (!photo) return;

    if (navigator.share) {
      navigator.share({
        title: photo.metadata?.originalName || photo.filename,
        text: `Confira esta foto: ${photo.metadata?.originalName || photo.filename}`,
        url: window.location.href,
      });
    } else {
      // Fallback: copiar URL para clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copiado",
        description: "O link da foto foi copiado para a área de transferência.",
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleImageError = () => {
    setImageError(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando foto...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!photo && !loading && !fetchError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando foto...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <FileImage className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Foto não encontrada.</p>
          <Button
            onClick={() => setLocation('/uploader/dashboard')}
            className="mt-4"
          >
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!photo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando foto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation('/uploader/dashboard')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar ao Dashboard</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Visualização da Foto</h1>
              <p className="text-muted-foreground">
                {photo.metadata?.originalName || photo.filename}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMetadata(!showMetadata)}
              className="flex items-center space-x-2"
            >
              {showMetadata ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>{showMetadata ? 'Ocultar' : 'Mostrar'} Metadados</span>
            </Button>

            <Button
              size="sm"
              onClick={handleDownload}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Imagem Principal */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                  {imageError ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <FileImage className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Erro ao carregar a imagem</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setImageError(false);
                            fetchPhoto();
                          }}
                          className="mt-2"
                        >
                          Tentar Novamente
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={photo.storageurl}
                      alt={photo.metadata?.originalName || photo.filename}
                      className="w-full h-full object-contain"
                      onError={handleImageError}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Informações da Foto */}
          <div className="space-y-6">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileImage className="h-5 w-5" />
                  <span>Informações</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Câmera:</span>
                  <span className="text-sm">{photo.camera_model || '-'}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Autor:</span>
                  <span className="text-sm">{photo.author || '-'}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Capturada:</span>
                  <span className="text-sm">
                    {photo.capture_datetime ? formatDate(photo.capture_datetime) : '-'}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Upload:</span>
                  <span className="text-sm">{formatDate(photo.uploadedat)}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Resolução:</span>
                  <Badge variant="outline" className="text-xs">
                    {photo.resolution}
                  </Badge>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Tamanho:</span>
                  <span className="text-sm">
                    {photo.metadata?.technical?.file_size_bytes ? formatFileSize(photo.metadata.technical.file_size_bytes) : '-'}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Arquivo Original:</span>
                  <span className="text-sm">{photo.original_filename || photo.filename}</span>
                </div>

                {photo.bibnumbers && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Numero do Peito:</span>
                    <span className="text-sm">{photo.bibnumbers}</span>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">Status:</span>
                  <Badge
                    variant={photo.processingstatus === 'Ready' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {photo.processingstatus}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Metadados Técnicos */}
            {showMetadata && photo.metadata?.technical && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Settings className="h-5 w-5" />
                    <span>Metadados Técnicos</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {Object.entries(photo.metadata.technical).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                        </span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Localização GPS */}
            {photo.metadata?.gpsLatitude && photo.metadata?.gpsLongitude && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5" />
                    <span>Localização</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Latitude:</span>
                      <span className="font-medium">{photo.metadata.gpsLatitude}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Longitude:</span>
                      <span className="font-medium">{photo.metadata.gpsLongitude}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => {
                        const url = `https://www.google.com/maps?q=${photo.metadata.gpsLatitude},${photo.metadata.gpsLongitude}`;
                        window.open(url, '_blank');
                      }}
                    >
                      Ver no Google Maps
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoPreviewPage;
